<template>
  <section class="trip-live-panel" :class="{ compact: !expanded }">
    <div class="live-header">
      <div>
        <p class="live-kicker">〇LIVE</p>
        <h2 class="live-title">{{ expanded ? headline : "現在の予定" }}</h2>
      </div>
      <span class="live-clock">{{ currentTimeLabel }}</span>
    </div>

    <div v-if="timelineItems.length === 0 && !isOutsideTrip" class="live-empty">
      <IconSvg name="calendar-solid-full" size="lg" />
      <div>
        <strong>現在表示できる予定がありません</strong>
        <p>スケジュールを追加すると、旅行中の現在予定がここに出ます。</p>
      </div>
    </div>

    <template v-else>
      <button
        v-if="!isOutsideTrip"
        class="live-status-card"
        :class="{ clickable: Boolean(currentItem) }"
        type="button"
        @click="openCurrentSchedule"
      >
        <div class="status-pill" :class="statusTone">
          {{ statusLabel }}
        </div>
        <div class="current-block">
          <span class="block-label">いま</span>
          <strong>{{ currentTitle }}</strong>
          <p>{{ currentSubText }}</p>
        </div>
        <div v-if="currentItem" class="progress-wrap">
          <div class="progress-track">
            <div class="progress-bar" :style="{ width: `${progressPercent}%` }"></div>
          </div>
          <span>{{ progressLabel }}</span>
        </div>
      </button>

      <div class="live-metrics" :class="{ 'reservation-only': isOutsideTrip }">
        <button
          v-if="!isOutsideTrip"
          type="button"
          class="live-metric"
          @click="openCurrentSchedule"
        >
          <span>今日の予定</span>
          <strong>{{ todayScheduleCount }}</strong>
        </button>
        <button
          type="button"
          class="live-metric"
          @click="openReservationSchedule"
        >
          <span>未予約</span>
          <strong :class="{ warning: pendingReservationCount > 0 }">
            {{ pendingReservationCount }}
          </strong>
        </button>
      </div>

      <div v-if="expanded && !isOutsideTrip" class="live-grid">
        <article class="live-info-box">
          <span class="box-label">次</span>
          <strong>{{ nextTitle }}</strong>
          <p>{{ nextSubText }}</p>
          <a
            v-if="nextMapUrl"
            :href="nextMapUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="map-link"
          >
            <IconSvg name="map-solid-full" size="xs" />
            地図
          </a>
        </article>

        <article class="live-info-box">
          <span class="box-label">出発目安</span>
          <strong>{{ departureTitle }}</strong>
          <p>{{ departureSubText }}</p>
        </article>

        <article class="live-info-box">
          <span class="box-label">確認</span>
          <strong>{{ reminderTitle }}</strong>
          <p>{{ reminderSubText }}</p>
        </article>
      </div>

      <ol v-if="expanded && !isOutsideTrip" class="live-timeline">
        <li
          v-for="item in nearbyItems"
          :key="`${item.source.LINE_NON}-${item.start.getTime()}`"
          :class="['timeline-item', item.state]"
        >
          <span class="timeline-time">{{ item.timeLabel }}</span>
          <div>
            <strong>{{ item.title }}</strong>
            <p>{{ item.placeText }}</p>
          </div>
        </li>
      </ol>

      <button
        v-if="!isOutsideTrip"
        class="open-schedule-btn"
        type="button"
        @click="openCurrentSchedule"
      >
        スケジュールで見る
      </button>
    </template>
  </section>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from "vue";
import IconSvg from "@/components/IconSvg.vue";
import {
  DEPARTURE_BUFFER_MINUTES,
  buildMapUrl,
  formatDateShort,
  formatDuration,
  formatTime,
  getProgressPercent,
  getTripLiveState,
  toDateKey,
} from "@/utils/trip-live.js";

export default {
  name: "TripLivePanel",
  components: {
    IconSvg,
  },
  props: {
    schedules: {
      type: Array,
      default: () => [],
    },
    shioriData: {
      type: Object,
      default: null,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["open-schedule"],
  setup(props, { emit }) {
    const now = ref(new Date());
    let timerId = null;

    onMounted(() => {
      timerId = window.setInterval(() => {
        now.value = new Date();
      }, 60000);
    });

    onUnmounted(() => {
      if (timerId) {
        window.clearInterval(timerId);
      }
    });

    const liveState = computed(() =>
      getTripLiveState(props.schedules, props.shioriData, now.value),
    );

    const timelineItems = computed(() => liveState.value.timelineItems);
    const currentItem = computed(() => liveState.value.currentItem);
    const nextItem = computed(() => liveState.value.nextItem);
    const previousItem = computed(() => liveState.value.previousItem);
    const tripState = computed(() => liveState.value.tripState);
    const isOutsideTrip = computed(() =>
      ["before-trip", "after-trip"].includes(tripState.value),
    );
    const currentTimeLabel = computed(() => formatTime(now.value));

    const headline = computed(() => {
      const labels = {
        current: "今の工程が進行中です",
        between: "次の工程まで待機中です",
        "before-trip": "旅行開始前です",
        "after-trip": "旅行は終了しています",
        done: "今日の工程は完了しています",
        waiting: "次の予定を確認できます",
      };
      return labels[tripState.value] || "現在の工程";
    });

    const statusLabel = computed(() => {
      if (currentItem.value) return "進行中";
      if (tripState.value === "before-trip") return "出発前";
      if (tripState.value === "after-trip") return "終了";
      if (nextItem.value) return "次の予定待ち";
      return "確認";
    });

    const statusTone = computed(() => {
      if (currentItem.value) return "is-current";
      if (nextItem.value) return "is-next";
      return "is-muted";
    });

    const currentTitle = computed(() => {
      if (currentItem.value) return currentItem.value.title;
      if (nextItem.value) return "現在進行中の予定はありません";
      if (previousItem.value) return previousItem.value.title;
      return "予定なし";
    });

    const currentSubText = computed(() => {
      if (currentItem.value) {
        return `${formatTime(currentItem.value.start)}から ${currentItem.value.placeText}`;
      }
      if (nextItem.value) {
        return `次は ${formatDateShort(nextItem.value.start)} ${formatTime(
          nextItem.value.start,
        )} から`;
      }
      if (previousItem.value) return "最後の工程まで完了しています";
      return "旅行日程とスケジュールを登録してください";
    });

    const nextTitle = computed(() => nextItem.value?.title || "次の予定なし");
    const nextSubText = computed(() => {
      if (!nextItem.value) return "追加の予定はありません";
      return `${formatDuration(nextItem.value.start - now.value)}後 / ${formatTime(
        nextItem.value.start,
      )}開始`;
    });
    const nextMapUrl = computed(() => buildMapUrl(nextItem.value));

    const departureDate = computed(() => {
      if (!nextItem.value) return null;
      return new Date(
        nextItem.value.start.getTime() - DEPARTURE_BUFFER_MINUTES * 60000,
      );
    });

    const departureTitle = computed(() => {
      if (!departureDate.value) return "予定なし";
      if (departureDate.value <= now.value) return "そろそろ出発";
      return formatTime(departureDate.value);
    });

    const departureSubText = computed(() => {
      if (!nextItem.value) return "次の予定があると出発目安を表示します";
      if (departureDate.value <= now.value) {
        return "次の予定に向かう時間です";
      }
      return `開始${DEPARTURE_BUFFER_MINUTES}分前を目安にしています`;
    });

    const reminderTitle = computed(() => {
      const target = currentItem.value || nextItem.value;
      if (!target) return "確認事項なし";
      if (target.source.RESERVATION_FLG === "1") {
        return target.source.RESERVATION_DONE_FLG === "1"
          ? "予約済み"
          : "予約未完了";
      }
      if (target.source.URL_NMV) return "リンクあり";
      return target.source.TRANSPORTATION_NMV || "メモ確認";
    });

    const reminderSubText = computed(() => {
      const target = currentItem.value || nextItem.value;
      if (!target) return "必要な確認があれば予定メモに残せます";
      if (target.source.RESERVATION_NAME) {
        return `予約名・番号: ${target.source.RESERVATION_NAME}`;
      }
      if (target.source.REMARKS) return target.source.REMARKS;
      if (target.source.URL_NMV) return "関連リンクをスケジュールから開けます";
      return "チケット・集合場所・持ち物を確認しておくと安心です";
    });

    const progressPercent = computed(() =>
      getProgressPercent(currentItem.value, now.value),
    );

    const progressLabel = computed(() => {
      if (!currentItem.value) return "";
      return `${formatDuration(currentItem.value.effectiveEnd - now.value)}後に次へ`;
    });

    const todaySchedules = computed(() => {
      const todayKey = toDateKey(now.value);
      return props.schedules.filter((item) => {
        const startKey = toDateKey(item.START_YMD);
        const endKey = toDateKey(item.END_YMD || item.START_YMD);
        return Boolean(
          startKey &&
            startKey <= todayKey &&
            todayKey <= (endKey || startKey) &&
            item.NEED_FLG !== "0",
        );
      });
    });

    const todayScheduleCount = computed(() => todaySchedules.value.length);

    const pendingReservationItems = computed(() =>
      props.schedules.filter(
        (item) =>
          item.RESERVATION_FLG === "1" &&
          item.RESERVATION_DONE_FLG !== "1" &&
          item.NEED_FLG !== "0",
      ),
    );

    const pendingReservationCount = computed(
      () => pendingReservationItems.value.length,
    );

    const nearbyItems = computed(() => {
      const currentIndex = timelineItems.value.findIndex(
        (item) => item === currentItem.value,
      );
      const nextIndex = timelineItems.value.findIndex(
        (item) => item === nextItem.value,
      );
      const baseIndex = currentIndex >= 0 ? currentIndex : Math.max(nextIndex, 0);
      return timelineItems.value
        .slice(Math.max(0, baseIndex - 1), baseIndex + 4)
        .map((item) => ({
          ...item,
          state:
            item === currentItem.value
              ? "current"
              : item.effectiveEnd <= now.value
                ? "past"
                : "future",
        }));
    });

    const emitSchedule = (source) => {
      emit("open-schedule", source?.LINE_NON || null);
    };

    const openCurrentSchedule = () => {
      emitSchedule(currentItem.value?.source || nextItem.value?.source);
    };

    const openReservationSchedule = () => {
      emitSchedule(pendingReservationItems.value[0]);
    };

    return {
      currentTimeLabel,
      timelineItems,
      headline,
      statusLabel,
      statusTone,
      isOutsideTrip,
      currentItem,
      currentTitle,
      currentSubText,
      nextTitle,
      nextSubText,
      nextMapUrl,
      departureTitle,
      departureSubText,
      reminderTitle,
      reminderSubText,
      progressPercent,
      progressLabel,
      todayScheduleCount,
      pendingReservationCount,
      nearbyItems,
      openCurrentSchedule,
      openReservationSchedule,
    };
  },
};
</script>

<style scoped>
.trip-live-panel {
  display: grid;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: white;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.trip-live-panel.compact {
  margin-bottom: 1rem;
}

.live-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.live-kicker {
  margin: 0 0 0.25rem;
  color: var(--primary-hover);
  font-size: 0.8125rem;
  font-weight: 800;
}

.live-title {
  margin: 0;
  color: var(--color-text);
  font-size: 1.25rem;
  line-height: 1.35;
}

.live-clock {
  flex-shrink: 0;
  padding: 0.35rem 0.6rem;
  background: var(--color-background);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  font-size: 0.875rem;
  font-weight: 700;
}

.live-empty {
  display: flex;
  gap: 0.875rem;
  align-items: center;
  padding: 1rem;
  background: var(--color-background);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
}

.live-empty strong {
  display: block;
  color: var(--color-text);
}

.live-empty p {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
}

.live-status-card,
.live-info-box {
  min-width: 0;
  padding: 1rem;
  background: var(--color-background);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
}

.live-status-card {
  display: block;
  width: 100%;
  color: inherit;
  font: inherit;
  text-align: left;
}

.live-status-card.clickable {
  cursor: pointer;
}

.live-status-card.clickable:hover {
  border-color: var(--primary-color);
  box-shadow: var(--shadow-sm);
}

.status-pill {
  display: inline-flex;
  margin-bottom: 0.75rem;
  padding: 0.25rem 0.6rem;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 800;
}

.status-pill.is-current {
  background: #dcfce7;
  color: #166534;
}

.status-pill.is-next {
  background: #eff6ff;
  color: #1d4ed8;
}

.status-pill.is-muted {
  background: #f3f4f6;
  color: #4b5563;
}

.current-block,
.live-info-box {
  display: grid;
  gap: 0.25rem;
}

.block-label,
.box-label {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.current-block strong,
.live-info-box strong {
  min-width: 0;
  color: var(--color-text);
  font-size: 1rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.current-block p,
.live-info-box p {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.progress-wrap {
  display: grid;
  gap: 0.4rem;
  margin-top: 0.875rem;
}

.progress-track {
  height: 8px;
  overflow: hidden;
  background: white;
  border-radius: var(--radius-full);
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-hover), var(--primary-color));
  border-radius: var(--radius-full);
}

.progress-wrap span {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.live-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.live-metrics.reservation-only {
  grid-template-columns: minmax(0, 1fr);
}

.live-metric {
  display: grid;
  gap: 0.25rem;
  min-width: 0;
  padding: 0.85rem;
  text-align: left;
  background: #fbfcfa;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  font: inherit;
}

.live-metric:hover {
  border-color: var(--primary-color);
  box-shadow: var(--shadow-sm);
}

.live-metric span {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.live-metric strong {
  color: var(--color-text);
  font-size: 1.25rem;
  line-height: 1;
}

.live-metric strong.warning {
  color: #c2410c;
}

.live-grid {
  display: grid;
  gap: 0.75rem;
}

.map-link {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  width: fit-content;
  margin-top: 0.35rem;
  color: var(--primary-hover);
  font-size: 0.8125rem;
  font-weight: 700;
  text-decoration: none;
}

.live-timeline {
  display: grid;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.timeline-item {
  display: grid;
  grid-template-columns: 5.5rem minmax(0, 1fr);
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--color-border-light);
  border-left: 4px solid var(--color-border-light);
  border-radius: var(--radius-md);
}

.timeline-item.current {
  border-left-color: var(--primary-hover);
  background: var(--color-primary-light);
}

.timeline-item.past {
  opacity: 0.62;
}

.timeline-time {
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  font-weight: 800;
}

.timeline-item strong {
  display: block;
  color: var(--color-text);
  overflow-wrap: anywhere;
}

.timeline-item p {
  margin: 0.2rem 0 0;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  overflow-wrap: anywhere;
}

.open-schedule-btn {
  justify-self: start;
  min-height: 38px;
  padding: 0.55rem 0.9rem;
  background: white;
  border: 1px solid var(--primary-color);
  border-radius: var(--radius-md);
  color: var(--primary-hover);
  font-weight: 800;
  cursor: pointer;
}

.open-schedule-btn:hover {
  background: var(--color-primary-light);
}

@media (min-width: 768px) {
  .trip-live-panel {
    padding: 1.5rem;
  }

  .live-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .trip-live-panel {
    padding: 1rem;
    border-radius: var(--radius-md);
  }

  .live-title {
    font-size: 1.0625rem;
  }

  .live-clock {
    font-size: 0.75rem;
  }

  .live-metrics {
    grid-template-columns: 1fr;
  }

  .timeline-item {
    grid-template-columns: 1fr;
  }
}
</style>
